import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAbandonedCartBulkMessagesRequest,
  fetchAbandonedCartBulkMessagesSuccess,
  fetchAbandonedCartBulkMessagesFailure,
  fetchAbandonedCartCustomersRequest,
  fetchAbandonedCartCustomersSuccess,
  fetchAbandonedCartCustomersFailure,
  createAbandonedCartBulkMessageRequest,
  createAbandonedCartBulkMessageSuccess,
  createAbandonedCartBulkMessageFailure,
  updateAbandonedCartBulkMessageRequest,
  updateAbandonedCartBulkMessageSuccess,
  updateAbandonedCartBulkMessageFailure,
  deleteAbandonedCartBulkMessageRequest,
  deleteAbandonedCartBulkMessageSuccess,
  deleteAbandonedCartBulkMessageFailure,
  sendAbandonedCartBulkMessageRequest,
  sendAbandonedCartBulkMessageSuccess,
  sendAbandonedCartBulkMessageFailure,
} from "../slices/abandonedCart.slice";

import {
  getAbandonedCartBulkMessages,
  getAbandonedCartCustomers,
  createAbandonedCartBulkMessage,
  updateAbandonedCartBulkMessage,
  deleteAbandonedCartBulkMessage,
  sendAbandonedCartBulkMessage,
} from "@/services/marketingService";

import {
  CreateAbandonedCartBulkMessageDto,
  UpdateAbandonedCartBulkMessageDto,
  FetchAbandonedCartOptions,
} from "@/types/models/marketing";
import { toApiError } from "@/lib/errorHelpers";

function* fetchAbandonedCartBulkMessagesSaga(
  action: PayloadAction<FetchAbandonedCartOptions>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      getAbandonedCartBulkMessages,
      action.payload,
    );
    yield put(
      fetchAbandonedCartBulkMessagesSuccess({
        data: response.data || response,
        pagination: response.pagination || {
          page: action.payload.page || 1,
          total: response.length || 0,
          pageSize: action.payload.page_size || 10,
          totalPages: Math.ceil(
            (response.length || 0) / (action.payload.page_size || 10),
          ),
        },
      }),
    );
  } catch (error: any) {
    yield put(fetchAbandonedCartBulkMessagesFailure(toApiError(error)));
  }
}

function* fetchAbandonedCartCustomersSaga(
  action: PayloadAction<FetchAbandonedCartOptions>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(getAbandonedCartCustomers, action.payload);
    yield put(
      fetchAbandonedCartCustomersSuccess({
        data: response.data || response,
        pagination: response.pagination || {
          page: action.payload.page || 1,
          total: response.length || 0,
          pageSize: action.payload.page_size || 10,
          totalPages: Math.ceil(
            (response.length || 0) / (action.payload.page_size || 10),
          ),
        },
      }),
    );
  } catch (error: any) {
    yield put(fetchAbandonedCartCustomersFailure(toApiError(error)));
  }
}

function* createAbandonedCartBulkMessageSaga(
  action: PayloadAction<CreateAbandonedCartBulkMessageDto>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      createAbandonedCartBulkMessage,
      action.payload,
    );
    yield put(createAbandonedCartBulkMessageSuccess(response));
  } catch (error: any) {
    yield put(createAbandonedCartBulkMessageFailure(toApiError(error)));
  }
}

function* updateAbandonedCartBulkMessageSaga(
  action: PayloadAction<{
    id: string;
    data: UpdateAbandonedCartBulkMessageDto;
  }>,
): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
    const response: any = yield call(updateAbandonedCartBulkMessage, id, data);
    yield put(updateAbandonedCartBulkMessageSuccess(response));
  } catch (error: any) {
    yield put(updateAbandonedCartBulkMessageFailure(toApiError(error)));
  }
}

function* deleteAbandonedCartBulkMessageSaga(
  action: PayloadAction<{ id: string }>,
): Generator<any, void, any> {
  try {
    yield call(deleteAbandonedCartBulkMessage, action.payload.id);
    yield put(deleteAbandonedCartBulkMessageSuccess({ id: action.payload.id }));
  } catch (error: any) {
    yield put(deleteAbandonedCartBulkMessageFailure(toApiError(error)));
  }
}

function* sendAbandonedCartBulkMessageSaga(
  action: PayloadAction<{ id: string }>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      sendAbandonedCartBulkMessage,
      action.payload.id,
    );
    yield put(sendAbandonedCartBulkMessageSuccess(response));
  } catch (error: any) {
    yield put(sendAbandonedCartBulkMessageFailure(toApiError(error)));
  }
}

export function* abandonedCartSaga(): Generator<any, void, any> {
  yield takeLatest(
    fetchAbandonedCartBulkMessagesRequest.type,
    fetchAbandonedCartBulkMessagesSaga,
  );
  yield takeLatest(
    fetchAbandonedCartCustomersRequest.type,
    fetchAbandonedCartCustomersSaga,
  );
  yield takeLatest(
    createAbandonedCartBulkMessageRequest.type,
    createAbandonedCartBulkMessageSaga,
  );
  yield takeLatest(
    updateAbandonedCartBulkMessageRequest.type,
    updateAbandonedCartBulkMessageSaga,
  );
  yield takeLatest(
    deleteAbandonedCartBulkMessageRequest.type,
    deleteAbandonedCartBulkMessageSaga,
  );
  yield takeLatest(
    sendAbandonedCartBulkMessageRequest.type,
    sendAbandonedCartBulkMessageSaga,
  );
}

export default abandonedCartSaga;
